/**
 * ClusterDetectionScreen — Token Intelligence dashboard.
 * Sidebar stays at fixed width; node selection shows details in sidebar without expanding it.
 */
import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown } from 'lucide-react';
import { useMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import ClusterTopNav from '@/components/clusterscope/ClusterTopNav';
import ClusterSidebar, { SidebarExpandTab } from '@/components/clusterscope/ClusterSidebar';
import ClusterGraph from '@/components/clusterscope/ClusterGraph';
import ClusterDrawer from '@/components/clusterscope/ClusterDrawer';
import type { Dataset, ClusterId, ScanContext } from '@/data/clusterscope-data';
import { parseTrailAmount } from '@/data/clusterscope-data';

const TOKEN_LOADING_STEPS = [
  'Fetching token information...',
  'Scanning holders...',
  'Detecting smart money...',
  'Building wallet clusters...',
  'Calculating metrics...',
  'Finalizing analysis...',
];

interface ClusterDetectionScreenProps {
  dataset: Dataset;
  scanContext: ScanContext;
  onNewSearch: () => void;
  onSwitchMode: (mode: 'token' | 'wallet') => void;
  onRescanToken?: (label: string) => Promise<void>;
}

export default function ClusterDetectionScreen({ dataset, scanContext, onNewSearch, onSwitchMode, onRescanToken }: ClusterDetectionScreenProps) {
  const isMobile = useMobile();
  const [mode] = useState<ScanContext['mode']>(scanContext.mode);
  const [searchValue, setSearchValue] = useState('');
  const [selectedClusterId, setSelectedClusterId] = useState<ClusterId | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [showCapitalTrace, setShowCapitalTrace] = useState(false);

  const walletsCount = dataset.graphNodes.length;
  const connectionsCount = dataset.graphEdges.length;

  const handleSelectCluster = (id: ClusterId | null) => {
    setSelectedClusterId(id);
    setMobileSidebarOpen(false);
  };

  const handleSelectNode = (id: string | null) => {
    setSelectedNodeId(id);
    if (id) {
      const node = dataset.graphNodes.find((n) => n.id === id);
      if (node) {
        // When a node is selected, show its cluster context but do NOT open the drawer
        // Node details are shown in the sidebar, keeping the graph fully visible
        setSelectedClusterId(null); // close drawer to keep graph visible
      }
    }
  };

  const handleSearchSubmit = async () => {
    if (!searchValue.trim() || !onRescanToken) return;
    setIsAnalyzing(true);
    setAnalysisStep(0);
    setSelectedClusterId(null);
    setSelectedNodeId(null);

    for (let i = 0; i < TOKEN_LOADING_STEPS.length; i++) {
      setAnalysisStep(i);
      await new Promise((r) => setTimeout(r, 650));
    }

    await onRescanToken(searchValue.trim());
    setIsAnalyzing(false);
    setSearchValue('');
  };

  // Capital trace data
  const capitalTraceData = useMemo(() => {
    const trails = dataset.fundingTrails;
    const totalInflow = trails.reduce((sum, t) => sum + parseTrailAmount(t.amount), 0);
    const totalOutflow = totalInflow * (0.6 + Math.random() * 0.2); // 60-80% of inflow
    const uniqueSources = new Set(trails.map((t) => t.sourceNodeId));
    const uniqueTargets = new Set(trails.map((t) => t.targetNodeId));

    const sourceMap = new Map<string, { label: string; amount: number }>();
    const targetMap = new Map<string, { label: string; amount: number }>();

    trails.forEach((t) => {
      const src = dataset.fundingWalletMap[t.sourceNodeId];
      const tgt = dataset.graphNodeMap[t.targetNodeId];
      const amt = parseTrailAmount(t.amount);

      const existingSrc = sourceMap.get(t.sourceNodeId);
      if (existingSrc) {
        existingSrc.amount += amt;
      } else {
        sourceMap.set(t.sourceNodeId, { label: src?.label ?? t.sourceNodeId, amount: amt });
      }

      const existingTgt = targetMap.get(t.targetNodeId);
      if (existingTgt) {
        existingTgt.amount += amt;
      } else {
        targetMap.set(t.targetNodeId, { label: tgt?.label ?? t.targetNodeId, amount: amt });
      }
    });

    // Top Funding Sources: exactly 5
    // First 2: top exchange hot wallets from actual trails
    const sortedExchangeSources = Array.from(sourceMap.entries())
      .map(([id, data]) => ({ id, ...data }))
      .sort((a, b) => b.amount - a.amount);
    const exchangeSources = sortedExchangeSources.slice(0, 2);

    // Remaining 3: external wallet addresses outside the analyzed network
    const externalSources = [
      { id: '7HxP...Gz8K', label: '7HxP...Gz8K', amount: 12400 },
      { id: 'Dm4Q...K9Lm', label: 'Dm4Q...K9Lm', amount: 8700 },
      { id: 'T8Xa...3RfN', label: 'T8Xa...3RfN', amount: 15600 },
    ];

    const topSources = [...exchangeSources, ...externalSources].slice(0, 5);

    // Top Recipients: 5 wallets from graph nodes, sampled across multiple clusters
    const byCluster = new Map<string, typeof dataset.graphNodes>();
    dataset.graphNodes.forEach((n) => {
      if (!byCluster.has(n.clusterId)) byCluster.set(n.clusterId, []);
      byCluster.get(n.clusterId)!.push(n);
    });

    const pickedRecipients: { id: string; label: string; amount: number; color: string }[] = [];
    const clusterIds = Array.from(byCluster.keys());
    let round = 0;
    while (pickedRecipients.length < 5 && round < 50) {
      const cid = clusterIds[round % clusterIds.length];
      const pool = byCluster.get(cid)!.filter((n) => !pickedRecipients.find((p) => p.id === n.id));
      if (pool.length > 0) {
        const node = pool[0];
        const cluster = dataset.clusterMap[cid];
        pickedRecipients.push({
          id: node.id,
          label: node.label,
          amount: 0,
          color: cluster?.color ?? '#14e0b0',
        });
        byCluster.set(cid, pool.slice(1));
      }
      round++;
    }

    // Assign realistic varied amounts (10–500 SOL)
    const recipientAmounts = [500, 320, 180, 95, 42];
    pickedRecipients.forEach((r, i) => {
      r.amount = recipientAmounts[i] ?? 5000;
    });

    const topRecipients = pickedRecipients.sort((a, b) => b.amount - a.amount);

    const topSourceExchange = exchangeSources[0]?.label ?? 'unknown exchange';
    const topSourceExchangeName = topSourceExchange.replace(' Hot', '');

    const insight = `${dataset.token.symbol} clusters show ${trails.length} distinct funding trails from ${uniqueSources.size} exchange hot wallets, with a total inflow of ${(totalInflow / 1000).toFixed(1)}K SOL. The majority of capital originates from ${topSourceExchangeName}, suggesting coordinated institutional accumulation followed by distribution across ${uniqueTargets.size} cluster wallets.`;

    return {
      totalInflow: `${(totalInflow / 1000).toFixed(1)}K SOL`,
      totalOutflow: `${(totalOutflow / 1000).toFixed(1)}K SOL`,
      fundingWallets: uniqueSources.size,
      recipientWallets: uniqueTargets.size,
      topSources,
      topRecipients,
      insight,
    };
  }, [dataset]);

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-background">
      <ClusterTopNav
        dataset={dataset}
        mode={mode}
        onModeChange={onSwitchMode}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearchSubmit={handleSearchSubmit}
        onNewSearch={onNewSearch}
        onToggleSidebar={isMobile ? () => setMobileSidebarOpen(true) : undefined}
        isAnalyzing={isAnalyzing}
        showCapitalTrace={showCapitalTrace}
        onToggleCapitalTrace={() => setShowCapitalTrace((v) => !v)}
      />

      <div className="relative flex flex-1 overflow-hidden">
        {/* Analysis Loading Overlay */}
        <AnimatePresence>
          {isAnalyzing && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/90 backdrop-blur-xl"
            >
              <div className="relative mb-10 h-16 w-16">
                <div className="absolute inset-0 rounded-full border-[3px] border-white/[0.08]" />
                <motion.div
                  className="absolute inset-0 rounded-full border-[3px] border-transparent"
                  style={{
                    borderTopColor: 'hsl(var(--primary))',
                    borderRightColor: 'hsl(var(--primary) / 0.35)',
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
                />
              </div>
              <div className="flex flex-col gap-3.5">
                {TOKEN_LOADING_STEPS.map((step, i) => {
                  const done = i < analysisStep;
                  const active = i === analysisStep;
                  return (
                    <div key={step} className="flex items-center gap-3">
                      <span className="relative flex h-4 w-4 items-center justify-center">
                        <motion.span
                          className="absolute inset-0 rounded-full"
                          animate={{
                            backgroundColor: done ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground) / 0.18)',
                            boxShadow: done
                              ? '0 0 10px 2px hsl(var(--primary) / 0.55)'
                              : '0 0 0px 0px transparent',
                          }}
                          transition={{ duration: 0.35 }}
                        />
                        {done && <Check className="relative h-2.5 w-2.5 text-background" strokeWidth={3} />}
                        {active && !done && (
                          <motion.span
                            className="absolute inset-0 rounded-full border border-primary/50"
                            animate={{ scale: [1, 1.6], opacity: [0.6, 0] }}
                            transition={{ repeat: Infinity, duration: 1 }}
                          />
                        )}
                      </span>
                      <span
                        className={`text-sm transition-colors duration-300 ${
                          done ? 'text-primary' : active ? 'text-foreground/80' : 'text-muted-foreground/50'
                        }`}
                      >
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!isMobile && (
          <>
            <ClusterSidebar
              dataset={dataset}
              selectedClusterId={selectedClusterId}
              selectedNodeId={selectedNodeId}
              onSelect={handleSelectCluster}
              collapsed={sidebarCollapsed}
              onToggleCollapse={() => setSidebarCollapsed(true)}
              walletsCount={walletsCount}
              connectionsCount={connectionsCount}
            />
            {sidebarCollapsed && <SidebarExpandTab onClick={() => setSidebarCollapsed(false)} />}
          </>
        )}

        <div className="relative flex-1">
          <ClusterGraph
            dataset={dataset}
            selectedClusterId={selectedClusterId}
            onSelectCluster={handleSelectCluster}
            selectedNodeId={selectedNodeId}
            onSelectNode={handleSelectNode}
          />

          {/* Capital Trace Panel */}
          <AnimatePresence>
            {showCapitalTrace && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="absolute bottom-0 left-0 right-0 z-20 overflow-hidden border-t border-white/10 bg-background/90 backdrop-blur-xl"
              >
                <div className="max-h-[45vh] overflow-y-auto p-4 sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Capital Trace</div>
                    <button
                      onClick={() => setShowCapitalTrace(false)}
                      className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2.5 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
                    >
                      <ChevronDown className="h-3 w-3" />
                      Collapse
                    </button>
                  </div>

                  {/* Header stats */}
                  <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Inflow</div>
                      <div className="text-sm font-bold text-[#14e0b0]">{capitalTraceData.totalInflow}</div>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total Outflow</div>
                      <div className="text-sm font-bold text-[#f472b6]">{capitalTraceData.totalOutflow}</div>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Funding Wallets</div>
                      <div className="text-sm font-bold text-foreground">{capitalTraceData.fundingWallets}</div>
                    </div>
                    <div className="rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recipient Wallets</div>
                      <div className="text-sm font-bold text-foreground">{capitalTraceData.recipientWallets}</div>
                    </div>
                  </div>

                  {/* Tables */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {/* Top Funding Sources */}
                    <div className="rounded-lg border border-white/10 bg-white/[0.03]">
                      <div className="border-b border-white/10 px-3 py-2 text-xs font-bold text-foreground">Top Funding Sources</div>
                      <div className="divide-y divide-white/5">
                        {capitalTraceData.topSources.map((s) => (
                          <div key={s.id} className="flex items-center justify-between px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full bg-[#a78bfa]" />
                              <span className="text-xs font-medium text-foreground">{s.label}</span>
                            </div>
                            <div className="text-xs font-mono text-muted-foreground">
                              {(s.amount / 1000).toFixed(1)}K SOL
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Top Recipients */}
                    <div className="rounded-lg border border-white/10 bg-white/[0.03]">
                      <div className="border-b border-white/10 px-3 py-2 text-xs font-bold text-foreground">Top Recipients</div>
                      <div className="divide-y divide-white/5">
                        {capitalTraceData.topRecipients.map((r) => (
                          <div key={r.id} className="flex items-center justify-between px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: r.color }} />
                              <span className="text-xs font-medium text-foreground">{r.label}</span>
                            </div>
                            <div className="text-xs font-mono text-muted-foreground">
                              {(r.amount / 1000).toFixed(1)}K SOL
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Capital Insight */}
                  <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-3">
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#a78bfa]">Capital Insight</div>
                    <p className="text-xs leading-relaxed text-muted-foreground">{capitalTraceData.insight}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {!isMobile && selectedClusterId && (
              <ClusterDrawer
                key="drawer-side"
                dataset={dataset}
                clusterId={selectedClusterId}
                selectedNodeId={selectedNodeId}
                onClose={() => setSelectedClusterId(null)}
                variant="side"
              />
            )}
          </AnimatePresence>
        </div>
      </div>

      {isMobile && (
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="w-[300px] border-white/10 bg-background p-0">
            <ClusterSidebar
              dataset={dataset}
              selectedClusterId={selectedClusterId}
              selectedNodeId={selectedNodeId}
              onSelect={handleSelectCluster}
              walletsCount={walletsCount}
              connectionsCount={connectionsCount}
            />
          </SheetContent>
        </Sheet>
      )}

      <AnimatePresence>
        {isMobile && selectedClusterId && (
          <ClusterDrawer
            key="drawer-bottom"
            dataset={dataset}
            clusterId={selectedClusterId}
            selectedNodeId={selectedNodeId}
            onClose={() => setSelectedClusterId(null)}
            variant="bottom"
          />
        )}
      </AnimatePresence>
    </div>
  );
}
